import { Box } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitList } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import FkField from '../common/MobileFormFields';
import { KitField, KitFormBar, KitInput, KitSwitch } from '../common/MobileKitExtras';

/**
 * Formulário de novo inventário no shell mobile — `prototipos-mobile/inventario/form.html`.
 * Só apresentação: valores, validação e envio vêm do `useFormik` de `pages/inventory/Form.js`.
 */

const CheckIcon = protoIcon('check');
const SearchIcon = protoIcon('search');

// Campo "select" que abre uma folha com busca e múltipla seleção (substitui o Autocomplete multiple).
function MultiPick({ label, options, value, onChange, getLabel }) {
    const [open, setOpen] = useState(false);
    const [term, setTerm] = useState('');
    const selected = new Set(value.map((v) => v.id));
    const filtered = options.filter((o) => getLabel(o).toLowerCase().includes(term.trim().toLowerCase()));
    const summary = value.length === 0 ? '' : value.length === 1 ? getLabel(value[0]) : `${value.length} selecionados`;

    const toggle = (option) => {
        onChange(selected.has(option.id) ? value.filter((v) => v.id !== option.id) : [...value, option]);
    };

    return (
        <KitField label={label}>
            <Box
                component="button"
                type="button"
                onClick={() => setOpen(true)}
                sx={(theme) => ({
                    width: '100%',
                    height: 50,
                    borderRadius: '14px',
                    border: '1.5px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    p: '0 14px',
                    fontSize: 16,
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    color: summary ? 'text.primary' : 'text.disabled',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:focus-visible': { borderColor: theme.palette.primary.main },
                })}
            >
                {summary || 'Todos'}
            </Box>
            <MobileSheet
                open={open}
                onClose={() => setOpen(false)}
                title={label}
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => onChange([])}>Limpar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => setOpen(false)}>Concluir</KitButton>
                    </>
                )}
            >
                <Box sx={{ position: 'relative', mb: '12px' }}>
                    <Box sx={{ position: 'absolute', left: 14, top: 0, bottom: 0, display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
                        <SearchIcon size={20} />
                    </Box>
                    <KitInput value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar…" aria-label={`Buscar ${label}`} sx={{ pl: '42px' }} />
                </Box>
                {filtered.length === 0 ? (
                    <Box sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', p: '16px 0' }}>Nenhum resultado.</Box>
                ) : (
                    <KitList>
                        {filtered.slice(0, 100).map((option) => (
                            <Box
                                key={option.id}
                                component="button"
                                type="button"
                                onClick={() => toggle(option)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    width: '100%',
                                    minHeight: 50,
                                    p: '10px 14px',
                                    border: 0,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    background: 'none',
                                    fontFamily: 'inherit',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    color: 'text.primary',
                                    cursor: 'pointer',
                                    boxSizing: 'border-box',
                                    '&:last-child': { borderBottom: 0 },
                                }}
                            >
                                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{getLabel(option)}</Box>
                                {selected.has(option.id) && <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex' }}><CheckIcon size={18} /></Box>}
                            </Box>
                        ))}
                    </KitList>
                )}
            </MobileSheet>
        </KitField>
    );
}

export default function MobileInventoryForm({ formik, categories, suppliers, products, onCancel }) {
    const partial = formik.values.scope === 'partial';

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={formik.handleSubmit} noValidate>
            <FkField formik={formik} name="description" label="Descrição" placeholder="Contagem geral outubro" onBlur={formik.handleBlur} />
            <FkField formik={formik} name="location_label" label="Estoque / local" onBlur={formik.handleBlur} />
            <FkField formik={formik} name="scope" label="Escopo" as="select">
                <option value="full">Completo (todos os produtos)</option>
                <option value="partial">Parcial (somente produtos filtrados)</option>
            </FkField>

            {partial && (
                <>
                    <MultiPick
                        label="Categorias"
                        options={categories}
                        value={formik.values.categories}
                        onChange={(v) => formik.setFieldValue('categories', v)}
                        getLabel={(o) => o.name || ''}
                    />
                    <MultiPick
                        label="Fornecedores"
                        options={suppliers}
                        value={formik.values.suppliers}
                        onChange={(v) => formik.setFieldValue('suppliers', v)}
                        getLabel={(o) => o.name || ''}
                    />
                    <MultiPick
                        label="Produtos"
                        options={products}
                        value={formik.values.products}
                        onChange={(v) => formik.setFieldValue('products', v)}
                        getLabel={(o) => (o.code ? `${o.code} - ${o.name}` : o.name || '')}
                    />
                    <FkField formik={formik} name="code" label="Código / SKU (contém)" />
                    <KitSwitch
                        title="Somente com estoque positivo"
                        checked={formik.values.only_positive_stock}
                        onChange={(e) => formik.setFieldValue('only_positive_stock', e.target.checked)}
                    />
                    <KitSwitch
                        title="Sem movimentação (90 dias)"
                        checked={formik.values.only_no_movement}
                        onChange={(e) => formik.setFieldValue('only_no_movement', e.target.checked)}
                    />
                    <KitSwitch
                        title="Abaixo do estoque mínimo"
                        checked={formik.values.only_below_min}
                        onChange={(e) => formik.setFieldValue('only_below_min', e.target.checked)}
                    />
                </>
            )}

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? 'Criando...' : 'Criar inventário'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
